import React, { createContext, useState, useContext, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { GET_THEATRICAL_DISCOUNT_INFO } from '@/services/Theatrical/queries'
import { GetTheatricalDiscountInfoQuery, GetTheatricalDiscountInfoQueryVariables } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage } from '@/utils/local-storage'

interface CowboyCountContextType {
  cowboyCount: number
  incrementCowboyCount: () => void
  decrementCowboyCount: () => void
  isLoading: boolean
}

const CowboyCountContext = createContext<CowboyCountContextType>({
  cowboyCount: 170,
  incrementCowboyCount: () => {},
  decrementCowboyCount: () => {},
  isLoading: false,
})

export const useCowboyCount = () => useContext(CowboyCountContext)

export const CowboyCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cowboyCount, setCowboyCount] = useState<number>(170)
  const [hasAdjustedCount, setHasAdjustedCount] = useState<boolean>(false)
  const track = useSafeTrack()

  const { data, loading } = useQuery<GetTheatricalDiscountInfoQuery, GetTheatricalDiscountInfoQueryVariables>(
    GET_THEATRICAL_DISCOUNT_INFO,
    {
      variables: {
        input: { code: 'FANCYCOWBOY' },
      },
    },
  )

  useEffect(() => {
    if (data?.discountByCode?.discount?.timesUsed && !hasAdjustedCount) {
      const apiCount = data.discountByCode.discount.timesUsed
      const adjustedCount = Math.ceil(apiCount * 2.26)
      const initialCount = adjustedCount > 170 ? adjustedCount : 170
      setCowboyCount(initialCount)

      const isOptedInStorage = getBooleanFromLocalStorage('COWBOY_UP_OPT_IN')
      if (!isOptedInStorage) {
        setHasAdjustedCount(true)
      }
    }
  }, [data, hasAdjustedCount])

  const incrementCowboyCount = () => {
    track('Cowbow Checkbox Clicked')
    setCowboyCount((prevCount) => prevCount + 1)
    setHasAdjustedCount(true)
  }

  const decrementCowboyCount = () => {
    setCowboyCount((prevCount) => Math.max(prevCount - 1, 170))
    setHasAdjustedCount(true)
  }

  return (
    <CowboyCountContext.Provider
      value={{
        cowboyCount,
        incrementCowboyCount,
        decrementCowboyCount,
        isLoading: loading,
      }}
    >
      {children}
    </CowboyCountContext.Provider>
  )
}
